/** XP, levels, contracts, factions and Marks: the progression layer. All data, no side effects. */

export const LEVEL_CAP = 40;

/** Cumulative XP required to reach `level` (level 1 = 0). Gentle quadratic. */
export function xpForLevel(level: number): number {
  const l = Math.max(1, Math.min(LEVEL_CAP, level)) - 1;
  return Math.round(100 * l + 18 * l * l);
}

export function levelForXp(xp: number): number {
  let level = 1;
  while (level < LEVEL_CAP && xp >= xpForLevel(level + 1)) level++;
  return level;
}

export interface RaidXpInput {
  kills: number;
  hymnKills: number;
  lootValue: number;
  extracted: boolean;
  durationSec: number;
}

export function raidXp(i: RaidXpInput): number {
  let xp = 100 + 20 * i.kills + 12 * i.hymnKills + Math.floor(i.lootValue / 10);
  if (i.extracted) xp += 150;
  xp += Math.min(120, Math.floor(i.durationSec / 10)); // up to +120 for a full raid
  return xp;
}

// ---------------------------------------------------------------- events & counters

/** Counter keys the event engine maintains per player. Marks and contracts reference these. */
export const Counter = {
  Kills: 'kills',
  Headshots: 'headshots',
  HymnKills: 'hymn_kills',
  ChoristerKills: 'kills_chorister',
  CantorKills: 'kills_cantor',
  SextonKills: 'kills_sexton',
  VergerKills: 'kills_verger',
  CantorChargeKills: 'kills_cantor_charging',
  Extracts: 'extracts',
  DimmingExtracts: 'extracts_dimming',
  RailExtracts: 'extracts_rail',
  LiftExtracts: 'extracts_lift',
  FerryExtracts: 'extracts_ferry',
  NoShotExtracts: 'extracts_noshot',
  WickEarned: 'wick_earned',
  ShardsExtracted: 'shards_extracted',
  CoresExtracted: 'cores_extracted',
  SkipJumps: 'skip_jumps',
  Mantles: 'mantles',
  FallsSurvived: 'falls_survived',
  Revives: 'revives',
  Deaths: 'deaths',
  Raids: 'raids',
  ContractsDone: 'contracts_done',
  QuestsDone: 'quests_done',
  ItemsCrafted: 'items_crafted',
  /** 1 once The Waking has been walked out of. See `onboarding.ts` for the grandfather rule. */
  TutorialDone: 'tutorial_done',
} as const;
export type CounterKey = (typeof Counter)[keyof typeof Counter];

// ---------------------------------------------------------------- factions

export const Faction = { Compact: 'compact', Hymnal: 'hymnal', Ferry: 'ferry' } as const;
export type FactionId = (typeof Faction)[keyof typeof Faction];
export const FACTION_NAMES: Record<FactionId, string> = {
  compact: 'Lantern Compact',
  hymnal: 'The Hymnal',
  ferry: 'The Ferry',
};
/** Rep thresholds for tiers 1..4 (tier 0 = neutral). */
export const FACTION_TIERS = [0, 200, 600, 1400, 3000] as const;
export const factionTier = (rep: number) => FACTION_TIERS.filter((t) => rep >= t).length - 1;

// ---------------------------------------------------------------- contracts

export interface ContractTemplate {
  id: string;
  title: string;
  /** Counter the contract tracks, and the goal. */
  counter: CounterKey;
  goal: number;
  faction: FactionId;
  reward: { wick: number; rep: number; xp: number; itemId?: string };
  /** 'daily' rotates at reset; 'weekly' is the Vigil. */
  cadence: 'daily' | 'weekly';
}

export const CONTRACTS: ContractTemplate[] = [
  {
    id: 'c_chor6',
    title: 'Thin the Choir',
    counter: 'kills_chorister',
    goal: 6,
    faction: 'hymnal',
    reward: { wick: 120, rep: 40, xp: 150 },
    cadence: 'daily',
  },
  {
    id: 'c_extract2',
    title: 'Come Home Twice',
    counter: 'extracts',
    goal: 2,
    faction: 'compact',
    reward: { wick: 150, rep: 50, xp: 200 },
    cadence: 'daily',
  },
  {
    id: 'c_rail',
    title: 'Ride the Spine',
    counter: 'extracts_rail',
    goal: 1,
    faction: 'compact',
    reward: { wick: 90, rep: 30, xp: 120 },
    cadence: 'daily',
  },
  {
    id: 'c_ferry',
    title: 'Nightward Passage',
    counter: 'extracts_ferry',
    goal: 1,
    faction: 'ferry',
    reward: { wick: 90, rep: 40, xp: 120 },
    cadence: 'daily',
  },
  {
    id: 'c_shards',
    title: 'Bring Us Their Voices',
    counter: 'shards_extracted',
    goal: 3,
    faction: 'hymnal',
    reward: { wick: 200, rep: 60, xp: 180 },
    cadence: 'daily',
  },
  {
    id: 'c_quiet',
    title: 'Not a Sound',
    counter: 'extracts_noshot',
    goal: 1,
    faction: 'ferry',
    reward: { wick: 250, rep: 60, xp: 220, itemId: 'mod_barrel_hush' },
    cadence: 'daily',
  },
  {
    id: 'c_cantor',
    title: 'Cut the Song Short',
    counter: 'kills_cantor_charging',
    goal: 2,
    faction: 'hymnal',
    reward: { wick: 160, rep: 50, xp: 160 },
    cadence: 'daily',
  },
  {
    id: 'c_wick',
    title: 'Pay the Lamps',
    counter: 'wick_earned',
    goal: 600,
    faction: 'compact',
    reward: { wick: 100, rep: 40, xp: 120 },
    cadence: 'daily',
  },
  {
    id: 'c_heads',
    title: 'Clean Work',
    counter: 'headshots',
    goal: 10,
    faction: 'ferry',
    reward: { wick: 140, rep: 40, xp: 160 },
    cadence: 'daily',
  },
  {
    id: 'v_dimming',
    title: 'Vigil: Through the Dimming',
    counter: 'extracts_dimming',
    goal: 3,
    faction: 'compact',
    reward: { wick: 600, rep: 150, xp: 600, itemId: 'candle_relic' },
    cadence: 'weekly',
  },
  {
    id: 'v_sexton',
    title: 'Vigil: Silence the Organ',
    counter: 'kills_sexton',
    goal: 4,
    faction: 'hymnal',
    reward: { wick: 700, rep: 180, xp: 700 },
    cadence: 'weekly',
  },
];

/** Deterministic daily rotation: 3 dailies + 1 weekly for a given day index. */
export function contractsForDay(dayIndex: number): ContractTemplate[] {
  const dailies = CONTRACTS.filter((c) => c.cadence === 'daily');
  const weeklies = CONTRACTS.filter((c) => c.cadence === 'weekly');
  const picks: ContractTemplate[] = [];
  for (let k = 0; k < 3; k++) picks.push(dailies[(dayIndex * 7 + k * 3) % dailies.length]!);
  picks.push(weeklies[Math.floor(dayIndex / 7) % weeklies.length]!);
  return picks;
}

// ---------------------------------------------------------------- Marks

export const MarkTier = { Bronze: 1, Amber: 2, Hymn: 3 } as const;

export interface MarkDef {
  id: string;
  name: string;
  counter: CounterKey;
  /** Thresholds for bronze / amber / hymn. */
  thresholds: [number, number, number];
  hidden?: boolean;
  /** Flavour shown on unlock. */
  flavour: string;
  /** Cosmetic reward at hymn tier. */
  reward?: { title?: string; lanternColor?: number };
}

export const MARKS: MarkDef[] = [
  {
    id: 'm_first_light',
    name: 'First Light',
    counter: 'extracts',
    thresholds: [1, 25, 100],
    flavour: 'You came back. Most do not.',
    reward: { title: 'Waker' },
  },
  {
    id: 'm_choirbreaker',
    name: 'Choirbreaker',
    counter: 'hymn_kills',
    thresholds: [10, 200, 1000],
    flavour: 'The song stops when you enter.',
    reward: { title: 'Choirbreaker', lanternColor: 0xdfe8ff },
  },
  {
    id: 'm_headhunter',
    name: 'Lamplighter',
    counter: 'headshots',
    thresholds: [10, 250, 1500],
    flavour: 'One light, put out at a time.',
  },
  {
    id: 'm_dimming',
    name: 'Dusk Walker',
    counter: 'extracts_dimming',
    thresholds: [1, 20, 75],
    flavour: 'When the Still went red, you kept walking.',
    reward: { title: 'Dusk Walker', lanternColor: 0xb36bff },
  },
  {
    id: 'm_quiet',
    name: 'Not a Sound',
    counter: 'extracts_noshot',
    thresholds: [1, 10, 40],
    flavour: 'The Choir never knew you were there.',
    reward: { title: 'the Quiet' },
  },
  {
    id: 'm_skip',
    name: 'Skipping Stone',
    counter: 'skip_jumps',
    thresholds: [50, 1000, 10000],
    flavour: 'Gravity is a suggestion.',
  },
  {
    id: 'm_mantle',
    name: 'Ledge Runner',
    counter: 'mantles',
    thresholds: [25, 500, 5000],
    flavour: 'Up is a direction too.',
  },
  {
    id: 'm_fall',
    name: 'Bad Landing',
    counter: 'falls_survived',
    thresholds: [5, 50, 250],
    flavour: 'That should have killed you.',
    hidden: true,
  },
  {
    id: 'm_warden',
    name: 'Warden of the Band',
    counter: 'revives',
    thresholds: [5, 100, 500],
    flavour: 'Nobody left behind.',
    reward: { title: 'Warden of the Band' },
  },
  {
    id: 'm_rich',
    name: 'Wick Baron',
    counter: 'wick_earned',
    thresholds: [1000, 25000, 250000],
    flavour: 'The lamps of Lanternfall burn on your account.',
    reward: { title: 'Baron' },
  },
  {
    id: 'm_cores',
    name: 'Heart Taker',
    counter: 'cores_extracted',
    thresholds: [1, 5, 25],
    flavour: 'You carried a mind out of the wreck.',
    reward: { title: 'Heart Taker', lanternColor: 0xc06be0 },
  },
  {
    id: 'm_cantor',
    name: 'Song Cutter',
    counter: 'kills_cantor_charging',
    thresholds: [3, 50, 300],
    flavour: 'Mid-verse.',
  },
  {
    id: 'm_sexton',
    name: 'Organ Stop',
    counter: 'kills_sexton',
    thresholds: [1, 25, 150],
    flavour: 'The pipes fell silent.',
  },
  {
    id: 'm_verger',
    name: 'Vergerslayer',
    counter: 'kills_verger',
    thresholds: [1, 5, 20],
    flavour: 'A choir without its conductor.',
    reward: { title: 'Vergerslayer', lanternColor: 0xff6ad5 },
  },
  {
    id: 'm_contracts',
    name: 'Reliable',
    counter: 'contracts_done',
    thresholds: [5, 50, 250],
    flavour: 'The Compact remembers who delivers.',
  },
  {
    id: 'm_crafter',
    name: "Tinker's Hands",
    counter: 'items_crafted',
    thresholds: [5, 100, 500],
    flavour: 'Nothing on the Band is finished.',
  },
  {
    id: 'm_raids',
    name: 'Old Hand',
    counter: 'raids',
    thresholds: [10, 100, 1000],
    flavour: 'You know the wreck better than the ones who built it.',
    reward: { title: 'Old Hand' },
  },
  {
    id: 'm_deaths',
    name: 'Kindling',
    counter: 'deaths',
    thresholds: [10, 100, 500],
    flavour: 'Every Waker is fuel eventually.',
    hidden: true,
  },
];

export function markTier(def: MarkDef, value: number): number {
  let tier = 0;
  for (const t of def.thresholds) if (value >= t) tier++;
  return tier;
}
